import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import { Alert, Form, Input, Modal, Spin } from 'antd';
import { JobTag } from '@/types';

type JobTagPopupProps = {
  data?: JobTag;
  onSubmit(user: JobTag): Promise<string | undefined>;
  onCancel(): void;
};

export const JobTagPopup: FC<JobTagPopupProps> = ({
  data,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<JobTag>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isAdd = data && !data.id;

  const handleSubmit = useCallback(
    async (values: JobTag) => {
      setErrorMessage(undefined);

      setLoading(true);
      const error = await onSubmit(values);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [onCancel, onSubmit],
  );

  useEffect(() => {
    setErrorMessage(undefined);
    form.resetFields();
    if (data) form.setFieldsValue(data);
  }, [data, form]);

  return (
    <Modal
      open={!!data}
      onCancel={onCancel}
      title={`${t(isAdd ? 'create' : 'edit')} Job Tag`}
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
    >
      <Spin spinning={loading}>
        <Form
          id="job-tag-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ flex: '0 0 80px' }}
          autoComplete="off"
        >
          <Form.Item<JobTag> name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item<JobTag>
            label="Tag"
            name="name"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={100} />
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
